import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, AlertTriangle, X, Flame } from 'lucide-react';

export const ServiceModal = ({ visible, type, onClose }) => {
  if (!visible) return null;

  let config = {};
  if (type === 'MAINTENANCE') {
    config = {
      icon: Wrench,
      iconColor: '#FFD700',
      title: 'Under Maintenance',
      description: 'Spark is currently down for some upgrades. We\'ll be back online shortly!',
      buttonText: null, // Blocking
      showClose: false,
    };
  } else if (type === 'TRAFFIC') {
    config = {
      icon: Flame,
      iconColor: '#FF4500',
      title: 'High Demand',
      description: 'We are getting more traffic than usual. Please try again after some time.',
      buttonText: null, // Blocking
      showClose: false,
    };
  } else if (type === 'TERMINATED') {
    config = {
      icon: AlertTriangle,
      iconColor: '#ff4444',
      title: 'Account Terminated',
      description: 'Your Account has been flagged by our system for violating our community guidelines therefore it has been terminated. If you think we made a mistake, please visit our website and submit a review appeal.',
      buttonText: 'Visit Support',
      showClose: true,
      onButtonPress: () => {
        window.open('https://sparkpl.com/info/contact', '_blank');
      }
    };
  } else {
      return null;
  }

  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
        />

        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative w-full max-w-[400px] bg-[#111] border border-[#333] rounded-3xl overflow-hidden shadow-2xl flex flex-col items-center pt-8 pb-6 px-6"
        >
          {config.showClose && (
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X size={20} className="text-gray-400 hover:text-white" />
            </button>
          )}

          {/* Background Glow */}
          <div 
            className="absolute -top-20 w-64 h-64 blur-[80px] rounded-full pointer-events-none opacity-20" 
            style={{ backgroundColor: config.iconColor }}
          />
          
          <div 
            className="w-20 h-20 rounded-full border-[1.5px] flex items-center justify-center mb-6 shadow-2xl relative z-10"
            style={{ borderColor: config.iconColor, backgroundColor: `${config.iconColor}15` }}
          >
              <IconComponent size={36} color={config.iconColor} strokeWidth={1.5} />
          </div>
          
          <h2 className="text-2xl font-extrabold text-white tracking-tight mb-3 text-center relative z-10">
            {config.title}
          </h2>
          
          {type === 'TERMINATED' ? (
              <p className="text-gray-400 text-center text-[15px] leading-relaxed mb-8 relative z-10">
                Your account has been flagged for violating our community guidelines and has been terminated. If you believe this is a mistake, please visit{' '}
                <a 
                  href="https://sparkpl.com/info/contact" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold underline"
                  style={{ color: config.iconColor }}
                >
                  our website
                </a>
                {' '}to submit an appeal for review.
              </p>
          ) : (
              <p className="text-gray-400 text-center text-[15px] leading-relaxed mb-8 relative z-10">
                {config.description}
              </p>
          )}

          {config.buttonText ? (
            <button
              onClick={config.onButtonPress}
              className="w-full text-white font-bold text-lg py-3.5 rounded-full hover:opacity-90 transition-opacity flex items-center justify-center relative z-10"
              style={{ backgroundColor: config.iconColor }}
            >
              {config.buttonText}
            </button>
          ) : (
            <div className="w-full flex justify-center py-2 relative z-10">
                <p className="text-gray-500 text-sm font-medium">Thank you for your patience!</p>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
